import type { Feature, FeatureCategory } from '@/types/content';

export const features: Feature[] = [
  // === OPERATIONS ===
  {
    id: 'scheduling',
    name: 'Smart Dispatch',
    tagline: 'Right tech. Right job. Every time.',
    description:
      'AI-powered dispatch that thinks like your best dispatcher. Considers skills, certifications, location, traffic, and customer preferences to make the optimal match.',
    icon: 'calendar',
    benefits: [
      'Drag-and-drop dispatch board with live status',
      'Intelligent technician matching',
      'Real-time GPS tracking',
      'Automated customer notifications',
      'Capacity planning & forecasting',
    ],
    screenshotUrl: '/images/screenshots/scheduling-dispatch.png',
  },
  {
    id: 'routes',
    name: 'Route Intelligence',
    tagline: 'Turn chaos into clockwork',
    description:
      'Whether it is recurring maintenance routes or dynamic daily scheduling, our route engine optimizes every mile. Build territories, balance workloads, and watch efficiency climb.',
    icon: 'map',
    benefits: [
      'Recurring route templates',
      'Dynamic daily optimization',
      'Territory management',
      'Multi-stop route planning',
      'Drive time predictions with traffic',
    ],
    screenshotUrl: '/images/features/routes.webp',
  },
  {
    id: 'mobile',
    name: 'Field App',
    tagline: 'The office that fits in your pocket',
    description:
      'A mobile experience your techs will actually want to use. Works offline in basements and rural areas. Syncs the moment you have signal.',
    icon: 'smartphone',
    benefits: [
      'True offline capability',
      'Photo & video documentation',
      'Digital signatures on glass',
      'On-site payment collection',
      'Integrated navigation',
    ],
    screenshotUrl: '/images/features/mobile.webp',
  },
  {
    id: 'fleet',
    name: 'Fleet Command',
    tagline: 'Keep your wheels turning',
    description:
      'Your vehicles are your lifeblood. Track maintenance, monitor costs, and prevent breakdowns before they strand your techs.',
    icon: 'truck',
    benefits: [
      'Preventive maintenance scheduling',
      'Fuel cost tracking & analysis',
      'Vehicle-to-tech assignment',
      'Digital inspection checklists',
      'Cost-per-mile reporting',
    ],
    screenshotUrl: '/images/screenshots/fleet-status-board.png',
  },
  {
    id: 'inventory',
    name: 'Parts & Equipment',
    tagline: 'Know what you have. Find it fast.',
    description:
      'Track every wrench, widget, and water heater across trucks, warehouses, and job sites. Real-time visibility means no more hunting for parts.',
    icon: 'package',
    benefits: [
      'Multi-location inventory tracking',
      'Truck stock management',
      'Automated reorder alerts',
      'Equipment lifecycle tracking',
      'Barcode & QR scanning',
    ],
    screenshotUrl: '/images/features/inventory.webp',
  },

  // === COMMUNICATION ===
  {
    id: 'spark',
    name: 'Spark Messenger',
    tagline: 'Instant connection. Zero friction.',
    description:
      'Real-time messaging built for the field. Share photos, locations, and updates instantly. Group channels for crews, direct messages for quick questions.',
    icon: 'message-circle',
    benefits: [
      'Team channels & direct messages',
      'Photo & file sharing',
      'Read receipts & presence',
      'Searchable message history',
      'Desktop & mobile sync',
    ],
    screenshotUrl: '/images/features/spark.webp',
  },
  {
    id: 'voip',
    name: 'Built-in Phone System',
    tagline: 'Your calls. Your CRM. Connected.',
    description:
      'VoIP phone system that lives inside Omniforge. Every call logged, every voicemail transcribed, every customer identified before you pick up.',
    icon: 'phone',
    benefits: [
      'Caller ID with customer lookup',
      'Call recording & transcription',
      'Voicemail-to-text',
      'Call routing & queues',
      'Click-to-call from anywhere',
    ],
    screenshotUrl: '/images/screenshots/clipboard-kanban.png',
  },
  {
    id: 'ai-receptionist',
    name: 'AI Receptionist',
    tagline: 'Never miss a call. Even at 2am.',
    description:
      'An AI that answers calls, books appointments, and handles routine inquiries with the warmth of your best CSR. Escalates complex issues to humans.',
    icon: 'bot',
    benefits: [
      '24/7 call answering',
      'Appointment scheduling',
      'FAQ handling',
      'Call screening & routing',
      'Seamless human handoff',
    ],
    screenshotUrl: '/images/features/ai-receptionist.webp',
  },

  // === SALES & CUSTOMERS ===
  {
    id: 'crm',
    name: 'Customer Intelligence',
    tagline: 'Know your customers like neighbors',
    description:
      'Every interaction, every piece of equipment, every preference—at your fingertips. Build relationships that turn one-time calls into lifetime customers.',
    icon: 'users',
    benefits: [
      'Complete service history',
      'Equipment tracking per property',
      'Communication timeline',
      'Custom fields & tags',
      'Customer portal access',
    ],
    screenshotUrl: '/images/features/crm.webp',
  },
  {
    id: 'pipelines',
    name: 'Sales Pipelines',
    tagline: 'Watch deals flow from lead to revenue',
    description:
      'Visual pipeline management that shows exactly where every opportunity stands. Drag deals through stages, set follow-up tasks, and forecast with confidence.',
    icon: 'git-branch',
    benefits: [
      'Kanban-style deal boards',
      'Stage automation & triggers',
      'Lead source tracking',
      'Win/loss analysis',
      'Revenue forecasting',
    ],
    screenshotUrl: '/images/features/pipelines.webp',
  },
  {
    id: 'estimates',
    name: 'Proposals That Win',
    tagline: 'Close faster with professional quotes',
    description:
      'Create stunning proposals with good-better-best options, photos, and financing. Send for e-signature and watch acceptance rates climb.',
    icon: 'file-text',
    benefits: [
      'Tiered pricing options',
      'Photo & video embedding',
      'E-signature capture',
      'One-click job conversion',
      'Proposal analytics',
    ],
    screenshotUrl: '/images/features/estimates.webp',
  },

  // === FINANCE ===
  {
    id: 'invoicing',
    name: 'Invoicing & Payments',
    tagline: 'Get paid faster. Period.',
    description:
      'From job completion to money in your account in hours, not weeks. One-click invoicing, multiple payment options, and automatic follow-ups.',
    icon: 'receipt',
    benefits: [
      'Instant invoice generation',
      'Cards, ACH, checks, financing',
      'Automated payment reminders',
      'Two-way QuickBooks sync',
      'Aging reports & collections',
    ],
    screenshotUrl: '/images/screenshots/invoices.png',
  },
  {
    id: 'profitability',
    name: 'Job Profitability',
    tagline: 'Know if you made money. On every job.',
    description:
      'Real-time job costing that tracks labor, parts, and overhead as the work happens. Stop guessing. Start knowing which jobs actually make money.',
    icon: 'trending-up',
    benefits: [
      'Live job cost tracking',
      'Labor cost by tech & task',
      'Parts margin analysis',
      'Overhead allocation',
      'Profitability by customer & type',
    ],
    screenshotUrl: '/images/screenshots/dashboard-overview.png',
  },

  // === WORK MANAGEMENT ===
  {
    id: 'workflows',
    name: 'Visual Workflow Builder',
    tagline: 'Design your processes. Watch them run.',
    description:
      'Drag-and-drop workflow designer that turns your best practices into automated processes. If-this-then-that logic that works while you sleep.',
    icon: 'git-merge',
    benefits: [
      'Visual drag-and-drop builder',
      'Conditional logic & branching',
      'Automated triggers & actions',
      'Approval workflows',
      'Audit trail for compliance',
    ],
    screenshotUrl: '/images/features/workflows.webp',
  },
  {
    id: 'tasks',
    name: 'Work Orchestration',
    tagline: 'Every task tracked. Nothing falls through.',
    description:
      'Task management built for service companies—not generic project tools. Assign, track, and complete work with context from jobs, customers, and techs.',
    icon: 'check-square',
    benefits: [
      'Kanban & list views',
      'Due dates & reminders',
      'Task templates',
      'Team workload visibility',
      'Job & customer linking',
    ],
    screenshotUrl: '/images/screenshots/clipboard-kanban.png',
  },
  {
    id: 'projects',
    name: 'Project Management',
    tagline: 'Big jobs. Total control.',
    description:
      'For installs, renovations, and multi-phase work that spans weeks or months. Track milestones, manage budgets, and keep customers in the loop.',
    icon: 'folder',
    benefits: [
      'Milestone tracking',
      'Budget vs. actual monitoring',
      'Gantt-style timeline views',
      'Customer progress portals',
      'Document management',
    ],
    screenshotUrl: '/images/features/projects.webp',
  },
  {
    id: 'quality',
    name: 'Quality Assurance',
    tagline: 'Six Sigma thinking. Service company execution.',
    description:
      'Process improvement tools inspired by Six Sigma methodology, adapted for field service. Reduce callbacks, improve first-time fix rates, and build consistency.',
    icon: 'shield-check',
    benefits: [
      'Quality checklists & audits',
      'Defect tracking & analysis',
      'Root cause investigation',
      'Process standardization',
      'Continuous improvement metrics',
    ],
    screenshotUrl: '/images/features/quality.webp',
  },

  // === TEAM & LEADERSHIP ===
  {
    id: 'hr',
    name: 'People Operations',
    tagline: 'Manage your team. Grow your people.',
    description:
      'Time tracking, PTO, payroll prep, and performance—all in one place. Treat your team right with tools that work.',
    icon: 'user-plus',
    benefits: [
      'GPS-verified time tracking',
      'PTO requests & approvals',
      'Payroll export for any provider',
      'Onboarding workflows',
      'Performance reviews',
    ],
    screenshotUrl: '/images/screenshots/recruiting-jobs.png',
  },
  {
    id: 'elevation',
    name: 'Leadership Elevation',
    tagline: 'Develop leaders. Transform your company.',
    description:
      'Structured programs that turn technicians into supervisors and supervisors into leaders. Weekly rhythms, accountability partners, and growth tracking.',
    icon: 'award',
    benefits: [
      'Leadership development tracks',
      'Mentorship pairing',
      'Goal setting & tracking',
      'Weekly accountability rhythms',
      'Career path visualization',
    ],
    screenshotUrl: '/images/screenshots/eos-accountability-chart.png',
  },
  {
    id: 'lms',
    name: 'Training Academy',
    tagline: 'Build skills. Certify competence.',
    description:
      'Full learning management system for onboarding, certifications, and ongoing training. Video courses, quizzes, and completion tracking.',
    icon: 'graduation-cap',
    benefits: [
      'Video & document courses',
      'Quizzes & assessments',
      'Certification tracking',
      'Compliance documentation',
      'Progress dashboards',
    ],
    screenshotUrl: '/images/features/lms.webp',
  },
  {
    id: 'business-rhythm',
    name: 'Business Rhythm',
    tagline: 'Run your company with discipline.',
    description:
      'Weekly meetings, quarterly rocks, annual planning, and team accountability—all built in. Create a consistent operating rhythm that keeps everyone aligned and moving forward.',
    icon: 'target',
    benefits: [
      'Structured weekly meeting runner',
      'Vision & strategy planning tools',
      'Quarterly goal (Rock) tracking',
      'Scorecard with automated KPIs',
      'Issues list with solve workflow',
      'Org chart & accountability',
      'Team health assessments',
      'Core process documentation',
    ],
    screenshotUrl: '/images/screenshots/eos-vto.png',
  },

  // === INTELLIGENCE ===
  {
    id: 'dashboards',
    name: 'Mission Control',
    tagline: 'Command your business from one screen',
    description:
      'Interactive dashboards that give you the pulse of your entire operation. Drill down from company-wide to individual jobs. Make decisions with data, not gut feelings.',
    icon: 'layout-dashboard',
    benefits: [
      'Real-time operational overview',
      'Customizable widget library',
      'Drill-down analytics',
      'Role-based dashboard views',
      'TV mode for office displays',
    ],
    screenshotUrl: '/images/screenshots/dashboard-overview.png',
  },
];

export const featureCategories: FeatureCategory[] = [
  {
    id: 'operations',
    name: 'Field Operations',
    description: 'Run your daily operations with precision',
    features: features.filter((f) =>
      ['scheduling', 'routes', 'mobile', 'fleet', 'inventory'].includes(f.id)
    ),
  },
  {
    id: 'communication',
    name: 'Communication',
    description: 'Stay connected with your team and customers',
    features: features.filter((f) => ['spark', 'voip', 'ai-receptionist'].includes(f.id)),
  },
  {
    id: 'sales',
    name: 'Sales & Customers',
    description: 'Grow revenue and build relationships',
    features: features.filter((f) => ['crm', 'pipelines', 'estimates'].includes(f.id)),
  },
  {
    id: 'finance',
    name: 'Finance & Profitability',
    description: 'Get paid and know your numbers',
    features: features.filter((f) => ['invoicing', 'profitability'].includes(f.id)),
  },
  {
    id: 'work',
    name: 'Work Management',
    description: 'Orchestrate complex work with clarity',
    features: features.filter((f) =>
      ['workflows', 'tasks', 'projects', 'quality'].includes(f.id)
    ),
  },
  {
    id: 'team',
    name: 'Team & Leadership',
    description: 'Build and develop your people',
    features: features.filter((f) => ['hr', 'elevation', 'lms', 'business-rhythm'].includes(f.id)),
  },
  {
    id: 'intelligence',
    name: 'Business Intelligence',
    description: 'See everything. Decide confidently.',
    features: features.filter((f) => ['dashboards'].includes(f.id)),
  },
];

// Grouped feature categories for the enhanced Features page
export const groupedCategories = {
  operations: {
    name: 'Operations',
    description: 'Streamline your daily operations with intelligent dispatch, route optimization, and real-time field coordination.',
    icon: 'calendar',
    categories: ['operations'],
  },
  finance: {
    name: 'Finance',
    description: 'From invoicing to profitability tracking, manage your finances with real-time visibility into every dollar.',
    icon: 'trending-up',
    categories: ['finance'],
  },
  fieldService: {
    name: 'Field Service',
    description: 'Equip your field technicians with powerful mobile tools, communication, and customer management capabilities.',
    icon: 'smartphone',
    categories: ['communication', 'sales'],
  },
  management: {
    name: 'Management',
    description: 'Lead your team effectively with HR tools, training systems, workflow automation, and business intelligence.',
    icon: 'layout-dashboard',
    categories: ['work', 'team', 'intelligence'],
  },
};

export function getFeatureById(id: string): Feature | undefined {
  return features.find((f) => f.id === id);
}

export function getFeaturesByCategory(categoryId: string): Feature[] {
  const category = featureCategories.find((c) => c.id === categoryId);
  return category?.features ?? [];
}
